import type { ApiClient } from '../generated/laravel-typed-openapi';
import { TypedStatusError } from '../generated/laravel-typed-openapi';
import { normalizePaginatedPublicChecklists, normalizePublicChecklistDetail } from './normalize';
import type { PublicChecklistsQuery } from './queryParams';
import type { PaginatedResponse, PublicChecklistDetailDto, PublicChecklistDto } from './types';

/**
 * The public checklist library over REST — the boundary between the generated client and the app.
 *
 * The responses arrive typed as `string` and `Array<unknown> | null` (B1), which is to say untyped;
 * `normalize.ts` is what turns them into DTOs, and these three functions are the only callers of it.
 * Everything above this file works in `PublicChecklistDto`.
 */

type FetchOptions = {
  signal?: AbortSignal;
};

const withSignal = ({ signal }: FetchOptions) => (signal ? { overrides: { signal } } : {});

export const fetchPublicChecklists = async (
  apiClient: ApiClient,
  query: PublicChecklistsQuery,
  options: FetchOptions = {},
): Promise<PaginatedResponse<PublicChecklistDto>> => {
  const response = await apiClient.get('/checklists/public', { query, ...withSignal(options) });

  return normalizePaginatedPublicChecklists(response, query.page_size);
};

export const fetchFeaturedPublicChecklists = async (
  apiClient: ApiClient,
  query: PublicChecklistsQuery,
  options: FetchOptions = {},
): Promise<PaginatedResponse<PublicChecklistDto>> => {
  const response = await apiClient.get('/checklists/featured', { query, ...withSignal(options) });

  return normalizePaginatedPublicChecklists(response, query.page_size);
};

const NOT_FOUND = 404;

/**
 * One public checklist and its items.
 *
 * The uuid is sent twice on purpose: the spec declares it both as a path parameter and as a
 * required query parameter (B2), and the generated client demands both. Drop the query copy once
 * the annotation is fixed.
 *
 * A 404 resolves to `null` instead of rejecting. A checklist that was unpublished or deleted since
 * the link was made is an ordinary outcome with a page of its own to show, not a failed request —
 * which is what being offline is, and that still rejects.
 */
export const fetchPublicChecklist = async (
  apiClient: ApiClient,
  uuid: string,
  options: FetchOptions = {},
): Promise<PublicChecklistDetailDto | null> => {
  try {
    const response = await apiClient.get('/checklists/public/{uuid}', {
      path: { uuid },
      query: { uuid },
      ...withSignal(options),
    });

    return normalizePublicChecklistDetail(response);
  } catch (error) {
    if (error instanceof TypedStatusError && error.status === NOT_FOUND) {
      return null;
    }

    throw error;
  }
};
